import React, { useState } from 'react';
import {
    Box,
    Grid,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
import { BreadCrumbs } from '../Common';
import AccessDenied from '../Common/Error/AccessDenied';
import BulkImportModal from './BulkImportModal';
import ImportRecentUploads from './ImportRecentUploads';
import UserInfo from '../../UserInfo';
import FeatureFlags, { useFeatureFlag } from '../../FeatureFlags';
import useBreadCrumbs from '../../hooks/useBreadCrumbs';
import useNavigate from '../../hooks/useNavigate';
import { trakrefFontFamily } from '../../utils';
import { trakrefBlue, Grey1, Grey3 } from '../../color_utils';

// ImportCustomerData feature flag + System Administrator or native System Owner admin roles only.
export const canImportCustomerData = () => (
    FeatureFlags.isEnabled('ImportCustomerData') && (
        UserInfo.isSystemAdministrator() || (
            UserInfo.isNativeSystemOwner() && UserInfo.isAdminRole()
        )
    )
);

// Tiles with an importType are also gated per-flag; off/missing renders the disabled "Coming Soon" state.
const importModules = [
    {
        title: "Import Locations",
        subtitle: "Stores, sites & service locations",
        Icon: LocationOnOutlinedIcon,
        importType: "locations",
        featureFlag: "ImportCustomerData_Location"
    },
    {
        title: "Import Inventory",
        subtitle: "Refrigerant systems & equipment",
        Icon: Inventory2OutlinedIcon,
        importType: "inventory",
        featureFlag: "ImportCustomerData_Inventory"
    },
    {
        title: "Import Users",
        subtitle: "Team members & access",
        Icon: GroupOutlinedIcon
    },
    {
        title: "Import Service Events",
        subtitle: "Past service & maintenance records",
        Icon: HomeRepairServiceOutlinedIcon
    }
];

const ImportCustomerData = () => {
    const hasAccess = canImportCustomerData();
    const navigate = useNavigate({ pageName: 'ImportCustomerData' });
    const [importModal, setImportModal] = useState(null);

    // Per-tile flags (hooks re-render the page when the flag map loads).
    const tileFlagEnabled = {
        ImportCustomerData_Location: useFeatureFlag('ImportCustomerData_Location'),
        ImportCustomerData_Inventory: useFeatureFlag('ImportCustomerData_Inventory'),
    };

    // Denied visits must not register the crumb in the session trail
    useBreadCrumbs({
        path: hasAccess
            ? { id: 'ImportCustomerData', text: 'Import Customer Data', link: window.location.pathname }
            : null
    });

    if (!hasAccess) {
        return <AccessDenied breadCrumbText="Import Customer Data" />;
    }

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Account Details', to: '/Account' },
                    { text: 'Import Customer Data' }
                ]}
            />
            <Grid
                container
                spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}
            >
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "24px"
                        }}
                    >
                        <IconButton aria-label="close" onClick={() => navigate.goBack('/Account')}>
                            <CloseIcon />
                        </IconButton>

                        <Typography
                            variant="h5"
                            sx={{ "&.MuiTypography-root": { fontFamily: trakrefFontFamily } }}
                            style={{ color: Grey1, fontSize: "1.375rem" }}
                        >
                            Import customer data
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Typography sx={{ fontFamily: trakrefFontFamily, fontSize: '20px', fontWeight: 500, color: Grey1 }}>
                        What would you like to import?
                    </Typography>
                    <Typography sx={{ fontFamily: trakrefFontFamily, fontSize: '13px', color: Grey3, marginBottom: '10px' }}>
                        Pick a type to get started. Each one uses its own template.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: "24px",
                        maxWidth: 1320
                    }}>
                        {importModules.map(({ title, subtitle, Icon, importType, featureFlag }) => {
                            const enabled = Boolean(importType) && (!featureFlag || tileFlagEnabled[featureFlag]);
                            const tile = (
                                <Box
                                    key={enabled ? title : undefined}
                                    onClick={enabled ? () => setImportModal(importType) : undefined}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "16px",
                                        flex: "1 1 0",
                                        minWidth: 240,
                                        padding: "24px 20px",
                                        backgroundColor: "#FFFFFF",
                                        border: "1px solid rgba(0, 0, 0, 0.12)",
                                        borderRadius: "8px",
                                        cursor: enabled ? "pointer" : "default",
                                        opacity: enabled ? 1 : 0.45,
                                        transition: "box-shadow 0.15s, transform 0.15s, border-color 0.15s",
                                        ...(enabled && {
                                            "&:hover": {
                                                borderColor: trakrefBlue,
                                                boxShadow: "0 6px 16px rgba(14, 140, 156, 0.22)",
                                                transform: "translateY(-3px)"
                                            }
                                        })
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: "12px",
                                            backgroundColor: trakrefBlue,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 24, color: "#FFFFFF" }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: trakrefFontFamily,
                                                fontSize: "17px",
                                                fontWeight: 500,
                                                color: "rgba(26, 23, 59, 0.87)",
                                                lineHeight: 1.2
                                            }}
                                        >
                                            {title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: trakrefFontFamily,
                                                fontSize: "12px",
                                                color: Grey3,
                                                lineHeight: 1.3,
                                                marginTop: "3px"
                                            }}
                                        >
                                            {subtitle}
                                        </Typography>
                                    </Box>
                                    <ChevronRightIcon sx={{ color: "#CAD4D9" }} />
                                </Box>
                            );
                            return enabled ? tile : (
                                <Tooltip key={title} title="Coming Soon" arrow>
                                    {tile}
                                </Tooltip>
                            );
                        })}
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <ImportRecentUploads />
                </Grid>
            </Grid>
            {importModal && (
                <BulkImportModal
                    open
                    importType={importModal}
                    onClose={() => setImportModal(null)}
                />
            )}
        </>
    );

}

export default ImportCustomerData;
